/**
 * Связь с расширением «Запись бага» (apps/extension) через его мост
 * content.js: команды и ответы ходят window.postMessage с меткой SOURCE.
 *
 * Расширение не обязано быть установлено: тогда моста нет, признака
 * data-bug-recorder на <html> нет, и форма предлагает его установить.
 */

import { useEffect, useState } from "react";

import { newRequestId } from "@taskflow/client-core";

export const SOURCE = "taskflow-bug-recorder";
/** Протокол, который понимает этот сайт; у расширения — PROTOCOL в background.js. */
export const PROTOCOL = 1;
/** Архив расширения кладёт сборка (vite.config.ts, plugin bugRecorderZip). */
export const EXTENSION_ZIP = `${import.meta.env.BASE_URL}downloads/bug-recorder.zip`;

const REPLY_TIMEOUT_MS = 3000;

export type ExtensionEvent =
  | { type: "started"; streamId: string; startedAt: number; tab: { title: string; url: string } }
  | { type: "cdp"; method: string; params: Record<string, unknown> }
  | { type: "body"; requestId: string; body: string; base64Encoded: boolean }
  | { type: "stopped"; reason: string }
  | { type: "error"; message: string };

interface Reply {
  ok: boolean;
  error?: string;
  version?: string;
  protocol?: number;
}

interface Envelope {
  source?: unknown;
  direction?: unknown;
  type?: unknown;
  replyTo?: unknown;
}

function isFromExtension(event: MessageEvent): Envelope | null {
  if (event.source !== window || event.origin !== window.location.origin) return null;
  const data = event.data as Envelope | null;
  if (data?.source !== SOURCE || data.direction !== "to-page") return null;
  return data;
}

/** Версия установленного расширения по признаку моста, или null. */
export function installedVersion(): string | null {
  return document.documentElement.dataset["bugRecorder"] ?? null;
}

/** Команда расширению; ответ или отказ по таймауту. */
export function callExtension(type: "hello" | "arm" | "stop" | "ping"): Promise<Reply> {
  return new Promise((resolve, reject) => {
    const id = newRequestId();
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", listen);
      reject(new Error("Расширение не отвечает — обновите страницу"));
    }, REPLY_TIMEOUT_MS);
    function listen(event: MessageEvent) {
      const data = isFromExtension(event);
      if (data?.type !== "reply" || data.replyTo !== id) return;
      window.clearTimeout(timer);
      window.removeEventListener("message", listen);
      const reply = data as unknown as Reply;
      if (reply.ok) resolve(reply);
      else reject(new Error(reply.error ?? "Расширение отказало"));
    }
    window.addEventListener("message", listen);
    window.postMessage({ source: SOURCE, direction: "to-extension", id, type }, window.location.origin);
  });
}

/** Подписка на события записи от расширения; возвращает отписку. */
export function onExtensionEvent(handler: (event: ExtensionEvent) => void): () => void {
  const listen = (event: MessageEvent) => {
    const data = isFromExtension(event);
    if (data === null || data.type === "reply") return;
    handler(data as unknown as ExtensionEvent);
  };
  window.addEventListener("message", listen);
  return () => window.removeEventListener("message", listen);
}

export type ExtensionStatus =
  | { kind: "checking" }
  | { kind: "missing" }
  /** Установлено, но протокол другой — нужен новый архив. */
  | { kind: "outdated"; version: string }
  | { kind: "ready"; version: string };

/** Есть ли расширение и подходит ли его версия. Проверяется при показе. */
export function useExtensionStatus(active: boolean): ExtensionStatus {
  const [status, setStatus] = useState<ExtensionStatus>({ kind: "checking" });
  // Признака моста нет — спрашивать некого, ответ известен сразу.
  const present = installedVersion() !== null;
  useEffect(() => {
    if (!active || !present) return;
    let cancelled = false;
    callExtension("hello").then(
      (reply) => {
        if (cancelled) return;
        const version = reply.version ?? "";
        setStatus(reply.protocol === PROTOCOL ? { kind: "ready", version } : { kind: "outdated", version });
      },
      () => {
        if (!cancelled) setStatus({ kind: "missing" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [active, present]);
  return present ? status : { kind: "missing" };
}
